import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import GeneratedFiles from '../GeneratedFiles';
import FeedbackModal from '../FeedbackModal';
import { submitFeedback, setAuthToken } from '../../services/api';

import CodeBlock from './CodeBlock';
import CollapsibleCodeExecution from './CollapsibleCodeExecution';
import ImageGenerationResult from './ImageGenerationResult';
import WebSearchResults from './WebSearchResults';
import SharePointResults, { SharePointStatus, mergeSharePointSources } from './SharePointResults';
import WebSearchNudge from './WebSearchNudge';
import {
  SnowflakeThinking,
  SnowflakeChart,
  SnowflakeTable,
  SnowflakeStatus,
  SnowflakeToolStatus,
} from '../SnowflakeContent';
import type { ChatMessageProps, SharePointResultsPart } from './types';

// ── Authenticated image loader ────────────────────────────────────────
// Fetches an image via download_url with auth headers and displays it.
const AuthImage: React.FC<{
  downloadUrl: string;
  alt: string;
  style?: React.CSSProperties;
  getAccessToken: () => Promise<string | null>;
}> = ({ downloadUrl, alt, style, getAccessToken }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    const absoluteUrl = downloadUrl.startsWith('http') ? downloadUrl : `${API_BASE_URL}${downloadUrl}`;

    (async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(absoluteUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) 
          return;

        const blob = await res.blob();

        if (revoked) 
          return;
        setBlobUrl(URL.createObjectURL(blob));
      } catch { /* ignore */ }
    })();

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [downloadUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!blobUrl) return <div style={{ ...style, backgroundColor: 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{alt}</div>;
  return <img src={blobUrl} alt={alt} style={style} />;
};

// ── Shared ReactMarkdown component overrides ──────────────────────────
// Avoids duplicating the same markdown renderers across branches.

function useMarkdownComponents() {
  const { colors } = useTheme();

  return useMemo(() => ({
    p: ({ children }: any) => <p style={{ margin: '0 0 8px 0' }}>{children}</p>,
    img: ({ src, alt }: any) => (
      <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', marginTop: '12px', marginBottom: '12px' }} />
    ),
    code: ({ className, children, ...props }: any) => {
      // Block code is handled by the `pre` override (CodeBlock wrapper).
      // Here we only style inline code.
      const isInline = !className;
      return isInline ? (
        <code style={{ backgroundColor: colors.surfaceElevated, padding: '2px 4px', borderRadius: '4px', fontSize: '0.9em' }} {...props}>{children}</code>
      ) : (
        <code {...props}>{children}</code>
      );
    },
    a: ({ href, children }: any) => {
      if (href && href.startsWith('/api/chat/download/')) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.primary, textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              const link = document.createElement('a');
              link.href = href;
              link.download = '';
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            {children}
          </a>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>{children}</a>;
    },
    pre: ({ children, node }: any) => {
      let language;
      if (node?.children?.[0]?.properties?.className) {
        const classes = node.children[0].properties.className as string[];
        const languageClass = classes.find((cls: string) => cls.startsWith('language-'));
        if (languageClass) language = languageClass.replace('language-', '');
      }
      return <CodeBlock language={language}>{children}</CodeBlock>;
    },
    ul: ({ children }: any) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
    ol: ({ children }: any) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
    li: ({ children }: any) => <li style={{ margin: '4px 0' }}>{children}</li>,
    h1: ({ children }: any) => <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: '12px 0 8px 0' }}>{children}</h1>,
    h2: ({ children }: any) => <h2 style={{ fontSize: '16px', fontWeight: 'bold', margin: '10px 0 6px 0' }}>{children}</h2>,
    h3: ({ children }: any) => <h3 style={{ fontSize: '15px', fontWeight: 'bold', margin: '8px 0 4px 0' }}>{children}</h3>,
    strong: ({ children }: any) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
    blockquote: ({ children }: any) => (
      <blockquote style={{ borderLeft: `3px solid ${colors.border}`, paddingLeft: '12px', margin: '8px 0', fontStyle: 'italic', color: colors.textMuted }}>{children}</blockquote>
    ),
    em: ({ children }: any) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
    table: ({ children }: any) => <table style={{ borderCollapse: 'collapse', width: '100%', margin: '8px 0', fontSize: '14px' }}>{children}</table>,
    thead: ({ children }: any) => <thead style={{ backgroundColor: colors.surfaceElevated }}>{children}</thead>,
    tbody: ({ children }: any) => <tbody>{children}</tbody>,
    tr: ({ children }: any) => <tr>{children}</tr>,
    th: ({ children }: any) => <th style={{ border: `1px solid ${colors.border}`, padding: '8px 12px', textAlign: 'left' as const, fontWeight: 'bold' }}>{children}</th>,
    td: ({ children }: any) => <td style={{ border: `1px solid ${colors.border}`, padding: '8px 12px' }}>{children}</td>,
  }), [colors]);
}

// ── Main component ────────────────────────────────────────────────────

const ChatMessage: React.FC<ChatMessageProps> = ({
  message, isLoading, isLastMessage, conversationId, agentId, messageIndex,
  modelProvider, modelName, modelApiVersion,
  onNudgeRetryWithSearch, onNudgeEnableAuto
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const { colors } = useTheme();
  const { getAccessToken } = useAuth();
  const markdownComponents = useMarkdownComponents();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownloadMessage = () => {
    try {
      const blob = new Blob([message.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `response-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to download text: ', err);
    }
  };

  const handleFeedbackSubmit = async (feedbackText: string, category?: string) => {
    if (!conversationId || messageIndex === undefined) return;
    try {
      const token = await getAccessToken();
      setAuthToken(token || '');
      await submitFeedback({
        conversation_id: conversationId,
        message_index: messageIndex,
        feedback_text: feedbackText,
        category,
        model_provider: modelProvider,
        model_name: modelName,
        model_api_version: modelApiVersion
      });
    } catch (err) {
      console.error('Failed to submit feedback: ', err);
      throw err;
    }
  };

  const handleDownload = async (file: { name: string; content?: string; type: string; download_url?: string }) => {
    try {
      if (file.content) {
        const byteCharacters = atob(file.content);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: file.type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.name;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (file.download_url) {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const absoluteUrl = file.download_url.startsWith('http')
          ? file.download_url
          : `${API_BASE_URL}${file.download_url}`;

        const accessToken = await getAccessToken();
        const response = await fetch(absoluteUrl, {
          method: 'GET',
          credentials: 'include',
          headers: { ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }) }
        });

        if (!response.ok)
          throw new Error('Failed to download file');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert('File download URL not available. Please try re-uploading the file.');
      }
    } catch (err) {
      console.error('Failed to download file: ', err);
      alert('Failed to download file. Please try again.');
    }
  };

  // ── Render content parts ─────────────────────────────────────────
  const renderParts = () => {
    if (!message.parts || message.parts.length === 0) return null;

    const sharePointParts = message.parts.filter(
      (p): p is SharePointResultsPart => p.type === 'sharepoint_results'
    );
    const firstSharePointIdx = message.parts.findIndex((p) => p.type === 'sharepoint_results');
    const isStreaming = isLoading && isLastMessage;

    return message.parts.map((part, idx) => {
      switch (part.type) {
        case 'text':
          return (
            <ReactMarkdown key={idx} remarkPlugins={[remarkGfm]} urlTransform={(url) => url} components={markdownComponents}>
              {part.content}
            </ReactMarkdown>
          );
        case 'code_execution':
          return (
            <CollapsibleCodeExecution key={idx}>
              <CodeBlock><code>{part.code}</code></CodeBlock>
            </CollapsibleCodeExecution>
          );
        case 'generated_files':
          return <GeneratedFiles key={idx} files={part.files} />;
        case 'image_generation':
          return <ImageGenerationResult key={idx} imageGeneration={part} />;
        case 'web_search_results':
          return <WebSearchResults key={idx} data={part} />;
        case 'sharepoint_results':
          return idx === firstSharePointIdx
            ? <SharePointResults key={idx} sources={mergeSharePointSources(sharePointParts)} />
            : null;
        case 'sharepoint_status':
          return isStreaming && idx === message.parts!.length - 1
            ? <SharePointStatus key={idx} status={part.status} />
            : null;
        case 'snowflake_thinking':
          return <SnowflakeThinking key={idx} text={part.text} />;
        case 'snowflake_chart':
          return <SnowflakeChart key={idx} spec={part.spec} />;
        case 'snowflake_table':
          return <SnowflakeTable key={idx} data={part} agentId={agentId} />;
        case 'snowflake_status':
          return <SnowflakeStatus key={idx} message={part.message} />;
        case 'snowflake_tool_status':
          return <SnowflakeToolStatus key={idx} message={part.message} />;
        case 'web_search_nudge':
          return (
            <WebSearchNudge
              key={idx}
              conversationId={part.conversation_id ?? conversationId}
              onRetryWithSearch={onNudgeRetryWithSearch}
              onEnableAuto={onNudgeEnableAuto}
            />
          );
        default:
          return null;
      }
    });
  };

  // ── Render message content ───────────────────────────────────────
  const renderContent = () => {
    if (message.role === 'assistant') {
      // Parts-based rendering
      if (message.parts && message.parts.length > 0) {
        return renderParts();
      }

      // Plain markdown fallback (for old messages or simple text)
      if (message.content) {
        return (
          <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={(url) => url} components={markdownComponents}>
            {message.content}
          </ReactMarkdown>
        );
      }

      // Loading indicator
      if (isLoading && isLastMessage) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {[0, 0.3, 0.6].map((delay, i) => (
              <div key={i} className="pulse-dot" style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: colors.textMuted, opacity: 0.7,
                animation: 'pulse 1.5s infinite', animationDelay: `${delay}s`,
              }} />
            ))}
            <style>{`@keyframes pulse { 0% { transform: scale(0.8); opacity: 0.5; } 50% { transform: scale(1.2); opacity: 0.8; } 100% { transform: scale(0.8); opacity: 0.5; } } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        );
      }

      return null;
    }

    // User message — plain text
    return <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>;
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: message.role === 'user' ? 'flex-end' : 'stretch',
      width: '100%'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: message.role === 'user' ? 'flex-end' : 'stretch',
        width: '100%'
      }}>
        <div style={{
          background: message.role === 'user' ? colors.userMessageBg : colors.assistantMessageBg,
          color: message.role === 'user' ? 'white' : colors.textPrimary,
          borderRadius: '18px',
          padding: '12px 16px',
          width: message.role === 'assistant' ? '100%' : undefined,
          maxWidth: message.role === 'user' ? '70%' : '100%',
          boxShadow: message.role === 'user' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
          wordBreak: 'break-word',
          lineHeight: '1.5',
          fontSize: '15px',
          border: message.role === 'assistant' ? `1px solid ${colors.borderLight}` : 'none',
        }}>
          {renderContent()}

          {/* Display attached files */}
          {message.files && message.files.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {message.files[0].type.startsWith('image/') ? (
                // Image files — show as a row of thumbnails
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {message.files.map((file, idx) => (
                    <div key={idx}>
                      {file.content ? (
                        <img
                          src={`data:${file.type};base64,${file.content}`}
                          alt={file.name}
                          style={{
                            maxWidth: '200px', maxHeight: '150px', borderRadius: '8px',
                            objectFit: 'cover', display: 'block',
                            border: message.role === 'user' ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)'
                          }}
                        />
                      ) : file.download_url ? (
                        <AuthImage
                          downloadUrl={file.download_url}
                          alt={file.name}
                          getAccessToken={getAccessToken}
                          style={{
                            maxWidth: '200px', maxHeight: '150px', borderRadius: '8px',
                            objectFit: 'cover', display: 'block',
                            border: message.role === 'user' ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '150px', height: '100px', borderRadius: '8px',
                          backgroundColor: message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', color: message.role === 'user' ? 'rgba(255,255,255,0.6)' : colors.textMuted
                        }}>
                          {file.name}
                        </div>
                      )}
                      <div style={{
                        fontSize: '11px',
                        color: message.role === 'user' ? 'rgba(255,255,255,0.7)' : colors.textMuted,
                        marginTop: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        maxWidth: '200px'
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        <button
                          onClick={() => handleDownload(file)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '2px 4px', borderRadius: '4px', fontSize: '11px',
                            color: message.role === 'user' ? 'rgba(255,255,255,0.8)' : colors.textMuted,
                            transition: 'background-color 0.2s ease', flexShrink: 0
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          title="Download file"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Non-image files
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                  {message.files.map((file, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center',
                      backgroundColor: message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                      borderRadius: '8px', padding: '6px 8px'
                    }}>
                      <span style={{ marginRight: '6px' }}>📄</span>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '13px', fontWeight: '500',
                          color: message.role === 'user' ? 'rgba(255,255,255,0.9)' : colors.textPrimary
                        }}>
                          {file.name}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: message.role === 'user' ? 'rgba(255,255,255,0.7)' : colors.textMuted
                        }}>
                          {file.size < 1024 ? `${file.size} B` : `${(file.size / 1024).toFixed(1)} KB`}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(file)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '4px 6px', borderRadius: '4px', fontSize: '14px',
                          color: message.role === 'user' ? 'rgba(255,255,255,0.8)' : colors.textMuted,
                          transition: 'background-color 0.2s ease', marginLeft: '6px'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        title="Download file"
                      >
                        ↓
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copy, Download, and Feedback buttons for assistant messages */}
      {message.role === 'assistant' && message.content && !isLoading && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          <button
            onClick={handleCopy}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px 8px', borderRadius: '12px', display: 'flex',
              alignItems: 'center', gap: '4px', fontSize: '12px',
              color: colors.textMuted, transition: 'all 0.2s ease',
              backgroundColor: copySuccess ? colors.successBg : 'transparent'
            }}
            onMouseEnter={(e) => { if (!copySuccess) e.currentTarget.style.backgroundColor = colors.buttonHover; }}
            onMouseLeave={(e) => { if (!copySuccess) e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Copy response"
          >
            {copySuccess ? (<><span style={{ fontSize: '14px' }}>✓</span><span>Copied</span></>) : (<><span style={{ fontSize: '14px' }}>📋</span><span>Copy</span></>)}
          </button>
          <button
            onClick={handleDownloadMessage}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px 8px', borderRadius: '12px', display: 'flex',
              alignItems: 'center', gap: '4px', fontSize: '12px',
              color: colors.textMuted, transition: 'all 0.2s ease',
              backgroundColor: downloadSuccess ? colors.successBg : 'transparent'
            }}
            onMouseEnter={(e) => { if (!downloadSuccess) e.currentTarget.style.backgroundColor = colors.buttonHover; }}
            onMouseLeave={(e) => { if (!downloadSuccess) e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Download response as .txt file"
          >
            {downloadSuccess ? (<><span style={{ fontSize: '14px' }}>✓</span><span>Downloaded</span></>) : (<><span style={{ fontSize: '14px' }}>💾</span><span>Download</span></>)}
          </button>
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px 8px', borderRadius: '12px', display: 'flex',
              alignItems: 'center', gap: '4px', fontSize: '12px',
              color: colors.textMuted, transition: 'all 0.2s ease',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            title="Send feedback about this response"
          >
            <span style={{ fontSize: '14px' }}>💬</span>
            <span>Send Feedback</span>
          </button>
        </div>
      )}

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default React.memo(ChatMessage);